import { useState } from 'react';
import { useTicketTaskTemplates } from '@/hooks/useTicketTemplates';
import { useTaskTypes } from '@/hooks/useTaskTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, GripVertical, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { TaskTemplateForm } from './TaskTemplateForm';

interface TicketTemplateTasksListProps {
  templateId: string;
}

export function TicketTemplateTasksList({ templateId }: TicketTemplateTasksListProps) {
  const { taskTemplates, isLoading, deleteTaskTemplate } = useTicketTaskTemplates(templateId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);

  if (isLoading) return <div>Loading tasks...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Template Tasks</h3>
        <Dialog open={isCreateOpen || !!editingTask} onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) setEditingTask(null);
        }}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Edit' : 'Add'} Task to Template</DialogTitle>
            </DialogHeader>
            <TaskTemplateForm 
              ticketTemplateId={templateId}
              taskTemplate={editingTask}
              onSuccess={() => {
                setIsCreateOpen(false);
                setEditingTask(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {taskTemplates && taskTemplates.length > 0 ? (
        <div className="space-y-2">
          {taskTemplates.map((task: any) => (
            <Card key={task.id}>
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <Badge variant="outline">{task.step_order}</Badge>
                  <div className="flex-1">
                    <div className="font-medium">{task.title_template}</div>
                    {task.description_template && (
                      <div className="text-sm text-muted-foreground">{task.description_template}</div>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    {task.trigger_type !== 'immediate' && (
                      <Badge variant="secondary">{task.trigger_type}</Badge>
                    )}
                    {task.estimated_duration_hours && (
                      <Badge variant="outline">{task.estimated_duration_hours}h</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingTask(task)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this task? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteTaskTemplate.mutateAsync(task.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No tasks configured. Click "Add Task" to get started.
        </div>
      )}
    </div>
  );
}
